import { CardListRow } from '@/components/Card/CardListRow.tsx';
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Container,
    Divider,
    FormControlLabel,
    Stack,
    Switch,
    Typography,
    useTheme,
} from '@mui/material';
import { useAuthentication } from '@/hooks/useAuthentication.ts';
import {
    useGetLiftOperatorQuery,
    usePutLiftOperatorMutation,
} from '@/api/v2/liftOperator.ts';
import { useEffect, useMemo, useState } from 'react';
import { set, cloneDeep } from 'lodash';
import { useNotification } from '@starjack/features/notifications';
import { LiftOperator } from '@/models/liftoperator.ts';
import { MarkdownInput } from '@starjack/shared-components';
import { TextField } from '@mui/material';

export const SettingsForm = () => {
    const theme = useTheme();
    const { liftOperatorData } = useAuthentication();

    const { data: liftOperator } = useGetLiftOperatorQuery({
        liftOperatorId: liftOperatorData.liftOperatorId,
    });
    const [putLiftOperator] = usePutLiftOperatorMutation();
    const [editLiftOperator, setEditLiftOperator] = useState<
        LiftOperator | undefined
    >(liftOperator);
    const { addNotification } = useNotification();

    const updateInfoText = (lang: 'de' | 'en', value: string) => {
        setEditLiftOperator((prev) => {
            if (!prev) return prev;
            const next = cloneDeep(prev);
            set(next, `infoText.${lang}`, value);
            return next;
        });
    };

    const updateTermsAndConditionsUrl = (lang: 'de' | 'en', value: string) => {
        setEditLiftOperator((prev) => {
            if (!prev) return prev;
            const next = cloneDeep(prev);
            set(next, `termsAndConditionsUrl.${lang}`, value);
            return next;
        });
    };

    const updateCancellationLock = (
        field:
            | 'cancellationLockEnabled'
            | 'cancellationLockDate'
            | 'cancellationLockDays',
        value: boolean | number[] | number | null
    ) => {
        setEditLiftOperator((prev) => {
            if (!prev) return prev;
            const next = cloneDeep(prev);
            set(next, field, value);
            return next;
        });
    };

    const onSave = () => {
        if (!editLiftOperator) {
            return;
        }

        putLiftOperator(editLiftOperator)
            .unwrap()
            .then(() => {
                addNotification({
                    type: 'success',
                    message: 'Einstellungen erfolgreich gespeichert',
                });
            })
            .catch(() => {
                addNotification({
                    type: 'error',
                    message: 'Fehler beim Speichern der Einstellungen',
                });
            });
    };

    useEffect(() => {
        setEditLiftOperator(liftOperator);
    }, [liftOperator]);

    const lockDateValue = useMemo(() => {
        const arr = editLiftOperator?.cancellationLockDate;
        if (!arr || !Array.isArray(arr)) return '';
        const [year, month, day] = arr;
        return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    }, [editLiftOperator?.cancellationLockDate]);

    const isConfigurationEnabled = liftOperator?.enableConfiguration ?? false;

    return (
        <>
            <Typography
                sx={{
                    mb: 8,
                }}
                variant='h4'
            >
                Allgemeine Einstellungen
            </Typography>
            <Stack spacing={theme.page.verticalSpacing}>
                <Container
                    sx={{
                        mt: 3,
                        ml: 'unset',
                    }}
                    maxWidth='lg'
                >
                    {!isConfigurationEnabled && liftOperator && (
                        <Alert severity='warning' sx={{ mb: 3 }}>
                            Die Konfiguration ist derzeit deaktiviert. Bitte
                            wenden Sie sich an Ihren Administrator, um die
                            Konfiguration zu aktivieren.
                        </Alert>
                    )}
                    <Card>
                        <CardContent
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                            }}
                        >
                            <Typography variant='h6'>
                                Bergbahn Einstellungen
                            </Typography>
                            <Button
                                sx={{
                                    ml: 'auto',
                                }}
                                onClick={onSave}
                                variant='contained'
                                disabled={!isConfigurationEnabled}
                            >
                                Speichern
                            </Button>
                        </CardContent>
                        <Divider
                            sx={{
                                width: '100%',
                            }}
                        />

                        <CardListRow
                            sx={{
                                position: 'relative',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'stretch',
                                pointerEvents: !isConfigurationEnabled
                                    ? 'none'
                                    : 'auto',
                                opacity: !isConfigurationEnabled ? 0.5 : 1,
                            }}
                            withBorderBottom
                        >
                            <Box sx={{ width: '100%', py: 2 }}>
                                <MarkdownInput
                                    label='Info Text Deutsch'
                                    value={editLiftOperator?.infoText?.de ?? ''}
                                    onChange={(value) =>
                                        updateInfoText('de', value)
                                    }
                                />
                            </Box>
                        </CardListRow>

                        <CardListRow
                            sx={{
                                position: 'relative',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'stretch',
                                pointerEvents: !isConfigurationEnabled
                                    ? 'none'
                                    : 'auto',
                                opacity: !isConfigurationEnabled ? 0.5 : 1,
                            }}
                            withBorderBottom
                        >
                            <Box sx={{ width: '100%', py: 2 }}>
                                <MarkdownInput
                                    label='Info Text Englisch'
                                    value={editLiftOperator?.infoText?.en ?? ''}
                                    onChange={(value) =>
                                        updateInfoText('en', value)
                                    }
                                />
                            </Box>
                        </CardListRow>

                        <CardListRow
                            sx={{
                                position: 'relative',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'stretch',
                                pointerEvents: !isConfigurationEnabled
                                    ? 'none'
                                    : 'auto',
                                opacity: !isConfigurationEnabled ? 0.5 : 1,
                            }}
                            withBorderBottom
                        >
                            <Box sx={{ width: '100%', py: 2 }}>
                                <TextField
                                    fullWidth
                                    label='AGB URL Deutsch'
                                    value={
                                        editLiftOperator?.termsAndConditionsUrl
                                            ?.de ?? ''
                                    }
                                    onChange={(e) =>
                                        updateTermsAndConditionsUrl(
                                            'de',
                                            e.target.value
                                        )
                                    }
                                />
                            </Box>
                        </CardListRow>

                        <CardListRow
                            sx={{
                                position: 'relative',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'stretch',
                                pointerEvents: !isConfigurationEnabled
                                    ? 'none'
                                    : 'auto',
                                opacity: !isConfigurationEnabled ? 0.5 : 1,
                            }}
                            withBorderBottom
                        >
                            <Box sx={{ width: '100%', py: 2 }}>
                                <TextField
                                    fullWidth
                                    label='AGB URL Englisch'
                                    value={
                                        editLiftOperator?.termsAndConditionsUrl
                                            ?.en ?? ''
                                    }
                                    onChange={(e) =>
                                        updateTermsAndConditionsUrl(
                                            'en',
                                            e.target.value
                                        )
                                    }
                                />
                            </Box>
                        </CardListRow>
                    </Card>

                    <Card sx={{ mt: 3 }}>
                        <CardContent
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                            }}
                        >
                            <Typography variant='h6'>
                                Periodensperre (Storno-Sperre)
                            </Typography>
                            <Button
                                sx={{
                                    ml: 'auto',
                                }}
                                onClick={onSave}
                                variant='contained'
                                disabled={!isConfigurationEnabled}
                            >
                                Speichern
                            </Button>
                        </CardContent>
                        <Divider
                            sx={{
                                width: '100%',
                            }}
                        />

                        <CardListRow
                            sx={{
                                pointerEvents: !isConfigurationEnabled
                                    ? 'none'
                                    : 'auto',
                                opacity: !isConfigurationEnabled ? 0.5 : 1,
                            }}
                            withBorderBottom
                        >
                            <Box sx={{ width: '100%', py: 2 }}>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={
                                                editLiftOperator?.cancellationLockEnabled ??
                                                false
                                            }
                                            onChange={(e) =>
                                                updateCancellationLock(
                                                    'cancellationLockEnabled',
                                                    e.target.checked
                                                )
                                            }
                                        />
                                    }
                                    label='Periodensperre aktivieren'
                                />
                                <Typography
                                    variant='body2'
                                    color='text.secondary'
                                    sx={{ mt: 1 }}
                                >
                                    Tickets, deren Bestelldatum vor dem
                                    Sperrdatum liegt, können von niemandem mehr
                                    storniert werden – auch nicht von
                                    Administratoren.
                                </Typography>
                            </Box>
                        </CardListRow>

                        {editLiftOperator?.cancellationLockEnabled && (
                            <>
                                <CardListRow
                                    sx={{
                                        pointerEvents: !isConfigurationEnabled
                                            ? 'none'
                                            : 'auto',
                                        opacity: !isConfigurationEnabled
                                            ? 0.5
                                            : 1,
                                    }}
                                    withBorderBottom
                                >
                                    <Box sx={{ width: '100%', py: 2 }}>
                                        <TextField
                                            fullWidth
                                            type='date'
                                            label='Festes Sperrdatum'
                                            InputLabelProps={{
                                                shrink: true,
                                            }}
                                            value={lockDateValue}
                                            onChange={(e) => {
                                                const val = e.target.value;
                                                if (!val) {
                                                    updateCancellationLock(
                                                        'cancellationLockDate',
                                                        null
                                                    );
                                                } else {
                                                    const [y, m, d] = val
                                                        .split('-')
                                                        .map(Number);
                                                    updateCancellationLock(
                                                        'cancellationLockDate',
                                                        [y, m, d]
                                                    );
                                                }
                                            }}
                                            helperText='Tickets mit Bestelldatum vor diesem Datum können nicht storniert werden.'
                                        />
                                    </Box>
                                </CardListRow>

                                <CardListRow
                                    sx={{
                                        pointerEvents: !isConfigurationEnabled
                                            ? 'none'
                                            : 'auto',
                                        opacity: !isConfigurationEnabled
                                            ? 0.5
                                            : 1,
                                    }}
                                    withBorderBottom
                                >
                                    <Box sx={{ width: '100%', py: 2 }}>
                                        <TextField
                                            fullWidth
                                            type='number'
                                            label='Rollendes Zeitfenster (Tage)'
                                            value={
                                                editLiftOperator?.cancellationLockDays ??
                                                ''
                                            }
                                            onChange={(e) =>
                                                updateCancellationLock(
                                                    'cancellationLockDays',
                                                    e.target.value
                                                        ? parseInt(
                                                              e.target.value
                                                          )
                                                        : null
                                                )
                                            }
                                            inputProps={{ min: 1 }}
                                            helperText='Tickets, die älter als diese Anzahl an Tagen sind, können nicht storniert werden.'
                                        />
                                    </Box>
                                </CardListRow>
                            </>
                        )}
                    </Card>
                </Container>
            </Stack>
        </>
    );
};
